import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { formatProjectNameWithType } from '../utils/project';
import type { ProjectDetail } from '../utils/commonUtils';
import { useEnsureBoardMenu } from '../context/BoardMenuContext';
import { fetchQnaBoard, getPostListPath, getPostMenuId } from '../utils/boardMenu';
import { 
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  X,
  Store,
  BarChart3,
  Activity,
  Package,
  Ticket,
  Users,
  User,
  MessageSquare,
  FileText,
  Settings
} from 'lucide-react';

interface ProjectSidebarProps {
  isDesktopSidebarOpen: boolean;
  setIsDesktopSidebarOpen: (open: boolean) => void;
  isMobileSidebarOpen: boolean;
  setIsMobileSidebarOpen: (open: boolean) => void;
  activeMenu: string;
  setActiveMenu: (menu: string) => void;
  projectDetail: ProjectDetail;
}

interface SubMenuItem {
  id: string;
  label: string;
  path: string;
}

interface MenuItem {
  id: string;
  label: string;
  icon: React.ReactNode;
  subItems?: SubMenuItem[];
}

const ProjectSidebar: React.FC<ProjectSidebarProps> = ({
  isDesktopSidebarOpen,
  setIsDesktopSidebarOpen,
  isMobileSidebarOpen,
  setIsMobileSidebarOpen,
  activeMenu,
  setActiveMenu,
  projectDetail
}) => {
  const navigate = useNavigate();
  const [expandedMenus, setExpandedMenus] = useState<Set<string>>(new Set());
  const [isPostEmptyModalOpen, setIsPostEmptyModalOpen] = useState(false);
  const [isQnaEmptyModalOpen, setIsQnaEmptyModalOpen] = useState(false);
  const projectDisplayName = formatProjectNameWithType(projectDetail.name, projectDetail.projectType);
  const boardSettingsPath = `/projects/${projectDetail.code}/boards`;
  const { boards: postBoards, isLoading: isPostBoardsLoading } = useEnsureBoardMenu(projectDetail.code);

  const openPostEmptyGuide = () => {
    setIsPostEmptyModalOpen(true);
    setIsMobileSidebarOpen(false);
  };

  const openQnaEmptyGuide = () => {
    setIsQnaEmptyModalOpen(true);
    setIsMobileSidebarOpen(false);
  };

  const navigateToQna = async () => {
    try {
      const qna = await fetchQnaBoard(projectDetail.code);
      if (qna) {
        setActiveMenu('qna');
        navigate(getPostListPath(projectDetail.code, qna.code));
        setIsMobileSidebarOpen(false);
      } else {
        openQnaEmptyGuide();
      }
    } catch {
      alert('1:1 문의 게시판 정보를 불러오지 못했습니다.');
    }
  };

  const goToBoardSettings = () => {
    setIsPostEmptyModalOpen(false);
    setIsQnaEmptyModalOpen(false);
    setActiveMenu('board-list');
    navigate(boardSettingsPath);
    setIsMobileSidebarOpen(false);
  };

  const postBoardSubItems: SubMenuItem[] = useMemo(() => {
    if (isPostBoardsLoading && postBoards.length === 0) {
      return [{ id: 'post-loading', label: '불러오는 중...', path: '' }];
    }
    if (postBoards.length === 0) {
      return [{ id: 'post-empty', label: '등록된 게시판 없음', path: '' }];
    }
    return postBoards.map((board) => ({
      id: getPostMenuId(board.code),
      label: board.name,
      path: getPostListPath(projectDetail.code, board.code),
    }));
  }, [postBoards, isPostBoardsLoading, projectDetail.code]);

  // 자동으로 매장 관리 메뉴 펼치기
  useEffect(() => {
    if (activeMenu === 'shop-list' || activeMenu === 'shop-category-group-list' || activeMenu === 'brand-list' || activeMenu === 'group-list') {
      setExpandedMenus(prev => {
        const newSet = new Set(prev);
        newSet.add('stores');
        return newSet;
      });
    }
  }, [activeMenu]);

  // 자동으로 설정 메뉴 펼치기
  useEffect(() => {
    if (activeMenu === 'project-config' || activeMenu === 'notification-channel-list' || activeMenu === 'brand-config' || activeMenu === 'app-list' || activeMenu === 'board-list') {
      setExpandedMenus(prev => {
        const newSet = new Set(prev);
        newSet.add('settings');
        return newSet;
      });
    }
  }, [activeMenu]);

  // 자동으로 멤버십 관리 메뉴 펼치기
  useEffect(() => {
    if (activeMenu === 'membership-list') {
      setExpandedMenus(prev => {
        const newSet = new Set(prev);
        newSet.add('membership');
        return newSet;
      });
    }
  }, [activeMenu]);

  // 자동으로 고객 관리 메뉴 펼치기
  useEffect(() => {
    if (activeMenu === 'pin-send-list') {
      setExpandedMenus(prev => {
        const newSet = new Set(prev);
        newSet.add('customers');
        return newSet;
      });
    }
  }, [activeMenu]);

  // 자동으로 상품 관리 메뉴 펼치기
  useEffect(() => {
    if (activeMenu === 'product-list' || activeMenu === 'product-category-group-list' || activeMenu === 'product-option-group-list' || activeMenu === 'module-position') {
      setExpandedMenus(prev => {
        const newSet = new Set(prev);
        newSet.add('products');
        return newSet;
      });
    }
  }, [activeMenu]);

  // 자동으로 교환권 관리 메뉴 펼치기
  useEffect(() => {
    if (activeMenu === 'voucher-list' || activeMenu === 'voucher-category-group-list') {
      setExpandedMenus(prev => {
        const newSet = new Set(prev);
        newSet.add('vouchers');
        return newSet;
      });
    }
  }, [activeMenu]);

  // 자동으로 게시판 관리 메뉴 펼치기
  useEffect(() => {
    if (activeMenu.startsWith('post-') && activeMenu !== 'post-loading' && activeMenu !== 'post-empty') {
      setExpandedMenus((prev) => {
        const newSet = new Set(prev);
        newSet.add('post-boards');
        return newSet;
      });
    }
  }, [activeMenu]);

  // Helper function for status badge
  const getStatusBadge = (status: ProjectDetail['status']) => {
    switch (status) {
      case 'ACTIVE':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
            <span className="w-1.5 h-1.5 mr-1 bg-green-400 rounded-full"></span>
            활성
          </span>
        );
      case 'INACTIVE':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
            <span className="w-1.5 h-1.5 mr-1 bg-gray-400 rounded-full"></span>
            비활성
          </span>
        );
      case 'PENDING':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
            <span className="w-1.5 h-1.5 mr-1 bg-yellow-400 rounded-full"></span>
            대기중
          </span>
        );
      case 'ARCHIVED':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
            <span className="w-1.5 h-1.5 mr-1 bg-red-400 rounded-full"></span>
            보관됨
          </span>
        );
    }
  };

  // Toggle accordion menu
  const toggleMenu = (menuId: string) => {
    const newExpanded = new Set(expandedMenus);
    if (newExpanded.has(menuId)) {
      newExpanded.delete(menuId);
    } else {
      newExpanded.add(menuId);
    }
    setExpandedMenus(newExpanded);
  };

  // Handle menu item clicks
  const handleMenuClick = (menuId: string) => {
    switch (menuId) {
      case 'overview':
        navigate(`/projects/${projectDetail.code}/dashboard`);
        break;
      case 'services':
        alert('준비중입니다.');
        break;
      case 'stores':
        // 매장 관리는 아코디언 토글만 수행
        toggleMenu('stores');
        break;
      case 'products':
        // 상품 관리는 아코디언 토글만 수행
        toggleMenu('products');
        break;
      case 'vouchers':
        toggleMenu('vouchers');
        break;
      case 'membership':
        // 멤버십 관리는 아코디언 토글만 수행
        toggleMenu('membership');
        break;
      case 'settings':
        // 설정은 아코디언 토글만 수행
        toggleMenu('settings');
        break;
      case 'customers':
        // 고객 관리는 아코디언 토글만 수행
        toggleMenu('customers');
        break;
      case 'qna':
        void navigateToQna();
        break;
      case 'post-boards':
        // 게시판 관리는 아코디언 토글만 수행 (하위 게시판명 클릭 시 이동)
        toggleMenu('post-boards');
        break;
      default:
        setActiveMenu(menuId);
        break;
    }
  };

  // Handle submenu item clicks
  const handleSubMenuClick = (path: string, subMenuId: string) => {
    if (path === '') {
      if (subMenuId === 'post-empty') {
        openPostEmptyGuide();
        return;
      }
      if (subMenuId === 'post-loading') {
        return;
      }
      alert('준비중입니다.');
      return;
    }
    navigate(path);
    setActiveMenu(subMenuId);
    setIsMobileSidebarOpen(false);
  };

  const getSubMenuButtonClass = (subItemId: string) => {
    if (subItemId === 'post-empty') {
      return 'w-full flex items-center px-3 py-2 rounded-lg transition-colors text-sm text-amber-700 hover:bg-amber-50';
    }
    if (subItemId === 'post-loading') {
      return 'w-full flex items-center px-3 py-2 rounded-lg transition-colors text-sm text-gray-400 cursor-default';
    }
    if (activeMenu === subItemId) {
      return 'w-full flex items-center px-3 py-2 rounded-lg transition-colors text-sm bg-gray-800 text-white';
    }
    return 'w-full flex items-center px-3 py-2 rounded-lg transition-colors text-sm text-gray-600 hover:bg-gray-100';
  };

  // Check if any submenu item is active
  const isSubMenuActive = (item: MenuItem): boolean => {
    if (!item.subItems) return false;
    return item.subItems.some(subItem => activeMenu === subItem.id);
  };

  // Menu items
  const menuItems: MenuItem[] = useMemo(() => [
    { id: 'overview', label: '대시보드', icon: <BarChart3 className="w-5 h-5" /> },
    // { id: 'services', label: '서비스 관리', icon: <Activity className="w-5 h-5" /> },
    { 
      id: 'stores', 
      label: '매장 관리', 
      icon: <Store className="w-5 h-5" />,
      subItems: [
        { id: 'brand-list', label: '브랜드 목록', path: '/shops/brands' },
        // { id: 'group-list', label: '그룹 목록', path: '/shops/groups' },
        { id: 'shop-list', label: '매장 목록', path: '/shops' },
        { id: 'shop-category-group-list', label: '카테고리/그룹 목록', path: '/shops/category-groups' }
      ]
    },
    { 
      id: 'products', 
      label: '상품 관리', 
      icon: <Package className="w-5 h-5" />,
      subItems: [
        { id: 'product-list', label: '상품 목록', path: '/products' },
        { id: 'product-category-group-list', label: '카테고리/그룹 목록', path: '/products/category-groups' },
        { id: 'product-option-group-list', label: '상품 옵션그룹 목록', path: '/products/option-groups' },
        // { id: 'module-position', label: '모듈 노출 위치 관리', path: '' }
      ]
    },
    {
      id: 'vouchers',
      label: '교환권 관리',
      icon: <Ticket className="w-5 h-5" />,
      subItems: [
        { id: 'voucher-list', label: '교환권 목록', path: '/vouchers' },
        { id: 'voucher-category-group-list', label: '카테고리/그룹 목록', path: '/vouchers/category-groups' }
      ]
    },
    { 
      id: 'membership', 
      label: '멤버십 관리', 
      icon: <Users className="w-5 h-5" />,
      subItems: [
        { id: 'membership-list', label: '멤버십 목록', path: '/memberships' }
      ]
    },
    {
      id: 'customers',
      label: '고객 관리',
      icon: <User className="w-5 h-5" />,
      subItems: [
        { id: 'pin-send-list', label: '핀 발송 조회', path: '/customers/pin-sends' }
      ]
    },
    { id: 'qna', label: '1:1 문의', icon: <MessageSquare className="w-5 h-5" /> },
    {
      id: 'post-boards',
      label: '게시판 관리',
      icon: <FileText className="w-5 h-5" />,
      subItems: postBoardSubItems,
    },
    {
      id: 'settings',
      label: '설정',
      icon: <Settings className="w-5 h-5" />,
      subItems: [
        { id: 'project-config', label: '프로젝트 설정', path: `/projects/${projectDetail.code}/config` },
        { id: 'notification-channel-list', label: '알림 채널', path: `/projects/${projectDetail.code}/notification-channel-settings` },
        { id: 'brand-config', label: '브랜드 설정', path: `/projects/${projectDetail.code}/brands` },
        { id: 'app-list', label: '앱 목록', path: `/projects/${projectDetail.code}/apps` },
        { id: 'board-list', label: '게시판 설정', path: `/projects/${projectDetail.code}/boards` },
      ],
    },
  ], [projectDetail.code, postBoardSubItems]);

  return (
    <>
      {/* Mobile Sidebar Overlay */}
      {isMobileSidebarOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={() => setIsMobileSidebarOpen(false)}
        />
      )}

      {/* Sidebar - Desktop/Tablet */}
      <aside className={`
        hidden lg:flex
        ${isDesktopSidebarOpen ? 'w-64' : 'w-16'}
        bg-white border-r border-gray-200 transition-[width] duration-300 flex-col relative h-screen
      `}>
        {/* Project Info Section */}
        <div className={`h-16 border-b border-gray-200 flex items-center ${
          isDesktopSidebarOpen ? 'px-4' : 'px-3'
        }`}>
          {isDesktopSidebarOpen ? (
            <div className="flex items-center space-x-3">
              {/* Project Image */}
              <div className="w-12 h-12 rounded-lg bg-gray-200 flex items-center justify-center flex-shrink-0 overflow-hidden">
                {projectDetail.logo ? (
                  <img 
                    src={projectDetail.logo} 
                    alt={projectDisplayName}
                    className="w-full h-full object-cover rounded-lg"
                    onError={(e) => {
                      e.currentTarget.style.display = 'none';
                      e.currentTarget.nextElementSibling?.classList.remove('hidden');
                    }}
                  />
                ) : null}
                <div className={`text-2xl ${projectDetail.logo ? 'hidden' : ''}`}>📁</div>
              </div>
              {/* Project Info */}
              <div className="flex-1 min-w-0">
                <h3 className="font-semibold text-gray-900 truncate mb-1">{projectDisplayName}</h3>
                <div className="-mt-1">
                  {getStatusBadge(projectDetail.status)}
                </div>
              </div>
            </div>
          ) : (
            <div className="flex justify-center">
              <div className="w-10 h-10 rounded-lg bg-gray-200 flex items-center justify-center overflow-hidden">
                {projectDetail.logo ? (
                  <img 
                    src={projectDetail.logo} 
                    alt={projectDisplayName}
                    className="w-full h-full object-cover rounded-lg"
                    onError={(e) => {
                      e.currentTarget.style.display = 'none';
                      e.currentTarget.nextElementSibling?.classList.remove('hidden');
                    }}
                  />
                ) : null}
                <div className={`text-xl ${projectDetail.logo ? 'hidden' : ''}`}>📁</div>
              </div>
            </div>
          )}
        </div>

        {/* Toggle Button for Desktop */}
        <button
          onClick={() => setIsDesktopSidebarOpen(!isDesktopSidebarOpen)}
          className="absolute -right-3 top-20 w-6 h-6 bg-white border border-gray-300 rounded-full shadow-md flex items-center justify-center hover:bg-gray-50 transition-colors z-10"
        >
          {isDesktopSidebarOpen ? (
            <ChevronLeft className="w-3 h-3 text-gray-600" />
          ) : (
            <ChevronRight className="w-3 h-3 text-gray-600" />
          )}
        </button>

        {/* Menu Items */}
        <nav className="flex-1 p-4 overflow-y-auto min-h-0">
          <ul className="space-y-2">
            {menuItems.map((item) => {
              const hasActiveSubMenu = isSubMenuActive(item);
              return (
              <li key={item.id}>
                <button
                  onClick={() => handleMenuClick(item.id)}
                  className={`w-full flex items-center ${
                    isDesktopSidebarOpen ? 'px-3 py-2' : 'p-2 justify-center'
                  } rounded-lg transition-colors ${
                    activeMenu === item.id && !item.subItems
                      ? 'bg-gray-900 text-white'
                      : hasActiveSubMenu
                      ? 'bg-gray-50 text-gray-900'
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                  title={!isDesktopSidebarOpen ? item.label : undefined}
                >
                  {item.icon}
                  {isDesktopSidebarOpen && (
                    <>
                      <span className="ml-3 flex-1 text-left">{item.label}</span>
                      {item.subItems && (
                        expandedMenus.has(item.id) ? (
                          <ChevronUp className="w-4 h-4" />
                        ) : (
                          <ChevronDown className="w-4 h-4" />
                        )
                      )}
                    </>
                  )}
                </button>
                {/* Submenu Items */}
                {isDesktopSidebarOpen && item.subItems && expandedMenus.has(item.id) && (
                  <ul className="mt-1 ml-4 space-y-1">
                    {item.subItems.map((subItem) => (
                      <li key={subItem.id}>
                        <button
                          onClick={() => handleSubMenuClick(subItem.path, subItem.id)}
                          className={getSubMenuButtonClass(subItem.id)}
                        >
                          <span className="ml-1">{subItem.label}</span>
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </li>
              );
            })}
          </ul>
        </nav>

        {/* Back to Projects Link */}
        {isDesktopSidebarOpen && (
          <div className="p-4 border-t border-gray-200 mt-auto flex-shrink-0">
            <button 
              onClick={() => navigate('/projects')}
              className="w-full text-sm text-gray-600 hover:text-gray-900 flex items-center"
            >
              <ChevronLeft className="w-4 h-4 mr-1" />
              프로젝트 목록으로
            </button>
          </div>
        )}
      </aside>

      {/* Sidebar - Mobile */}
      <aside className={`
        fixed lg:hidden inset-y-0 left-0 z-50
        ${isMobileSidebarOpen ? 'translate-x-0' : '-translate-x-full'}
        w-64 bg-white border-r border-gray-200 transition-[transform] duration-300 flex flex-col
      `}>
        {/* Project Info Section */}
        <div className="p-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3 flex-1 min-w-0">
              {/* Project Image */}
              <div className="w-12 h-12 rounded-lg bg-gray-200 flex items-center justify-center flex-shrink-0 overflow-hidden">
                {projectDetail.logo ? (
                  <img 
                    src={projectDetail.logo} 
                    alt={projectDisplayName}
                    className="w-full h-full object-cover rounded-lg"
                    onError={(e) => {
                      e.currentTarget.style.display = 'none';
                      e.currentTarget.nextElementSibling?.classList.remove('hidden');
                    }}
                  />
                ) : null}
                <div className={`text-2xl ${projectDetail.logo ? 'hidden' : ''}`}>📁</div>
              </div>
              {/* Project Info */}
              <div className="flex-1 min-w-0">
                <h3 className="font-semibold text-gray-900 truncate">{projectDisplayName}</h3>
                <div className="mt-1">
                  {getStatusBadge(projectDetail.status)}
                </div>
              </div>
            </div>
            <button
              onClick={() => setIsMobileSidebarOpen(false)}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors ml-2"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Menu Items */}
        <nav className="flex-1 p-4 overflow-y-auto min-h-0">
          <ul className="space-y-2">
            {menuItems.map((item) => {
              const hasActiveSubMenu = isSubMenuActive(item);
              return (
              <li key={item.id}>
                <button
                  onClick={() => {
                    if (item.subItems) {
                      toggleMenu(item.id);
                    } else {
                      handleMenuClick(item.id);
                      setIsMobileSidebarOpen(false);
                    }
                  }}
                  className={`w-full flex items-center px-3 py-2 rounded-lg transition-colors ${
                    activeMenu === item.id && !item.subItems
                      ? 'bg-gray-900 text-white'
                      : hasActiveSubMenu
                      ? 'bg-gray-50 text-gray-900'
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  {item.icon}
                  <span className="ml-3 flex-1 text-left">{item.label}</span>
                  {item.subItems && (
                    expandedMenus.has(item.id) ? (
                      <ChevronUp className="w-4 h-4" />
                    ) : (
                      <ChevronDown className="w-4 h-4" />
                    )
                  )}
                </button>
                {/* Submenu Items */}
                {item.subItems && expandedMenus.has(item.id) && (
                  <ul className="mt-1 ml-4 space-y-1">
                    {item.subItems.map((subItem) => (
                      <li key={subItem.id}>
                        <button
                          onClick={() => handleSubMenuClick(subItem.path, subItem.id)}
                          className={getSubMenuButtonClass(subItem.id)}
                        >
                          <span className="ml-1">{subItem.label}</span>
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </li>
              );
            })}
          </ul>
        </nav>

        {/* Back to Projects Link */}
        <div className="p-4 border-t border-gray-200 flex-shrink-0">
          <button 
            onClick={() => {
              navigate('/projects');
              setIsMobileSidebarOpen(false);
            }}
            className="w-full text-sm text-gray-600 hover:text-gray-900 flex items-center"
          >
            <ChevronLeft className="w-4 h-4 mr-1" />
            프로젝트 목록으로
          </button>
        </div>
      </aside>

      {isQnaEmptyModalOpen && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4"
          onClick={() => setIsQnaEmptyModalOpen(false)}
        >
          <div
            className="bg-white rounded-xl shadow-xl max-w-md w-full p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-4 mb-4">
              <h3 className="text-lg font-semibold text-gray-900">1:1 문의 게시판이 없습니다</h3>
              <button
                type="button"
                onClick={() => setIsQnaEmptyModalOpen(false)}
                className="text-gray-400 hover:text-gray-600 shrink-0"
                aria-label="닫기"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <p className="text-sm text-gray-600 leading-relaxed">
              설정 &gt; 게시판 설정에서 게시판 코드가 <strong>QNA</strong>인 게시판을 추가해 주세요.
              <br />
              추가 후 1:1 문의 메뉴에서 문의 게시물을 관리할 수 있습니다.
            </p>
            <div className="mt-6 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setIsQnaEmptyModalOpen(false)}
                className="px-4 py-2 text-sm text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                닫기
              </button>
              <button
                type="button"
                onClick={goToBoardSettings}
                className="px-4 py-2 text-sm text-white bg-gray-900 rounded-lg hover:bg-gray-800"
              >
                게시판 설정으로 이동
              </button>
            </div>
          </div>
        </div>
      )}

      {isPostEmptyModalOpen && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4"
          onClick={() => setIsPostEmptyModalOpen(false)}
        >
          <div
            className="bg-white rounded-xl shadow-xl max-w-md w-full p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-4 mb-4">
              <h3 className="text-lg font-semibold text-gray-900">게시판이 없습니다</h3>
              <button
                type="button"
                onClick={() => setIsPostEmptyModalOpen(false)}
                className="text-gray-400 hover:text-gray-600 shrink-0"
                aria-label="닫기"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <p className="text-sm text-gray-600 leading-relaxed">
              설정 &gt; 게시판 설정에서 게시판을 추가해 주세요.
              <br />
              게시판을 등록하면 게시판 관리 메뉴에서 게시물을 관리할 수 있습니다.
            </p>
            <div className="mt-6 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setIsPostEmptyModalOpen(false)}
                className="px-4 py-2 text-sm text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                닫기
              </button>
              <button
                type="button"
                onClick={goToBoardSettings}
                className="px-4 py-2 text-sm text-white bg-gray-900 rounded-lg hover:bg-gray-800"
              >
                게시판 설정으로 이동
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ProjectSidebar;
